import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { SERVICE_AGREEMENT_STATUSES } from "@/contracts";

import { serviceAgreementsQuery, type ServiceAgreementFilters } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDate } from "@/lib/format";
import { useListFilters } from "@/lib/use-list-filters";

import { ServiceAgreementCreateDialog } from "./service-agreement-create-dialog";
import { SERVICE_STATUS_LABELS, SERVICE_STATUS_TONES } from "./service-labels";

/**
 * When `embedded`, the contracts hub owns the page title; this panel only
 * renders filters, the table and the draw-up action.
 */
export function ServiceAgreementsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation("services");
  const client = useApiClient();
  const { user } = useAuth();
  const canDraw = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [drawing, setDrawing] = useState(false);

  const status = filterValue("status");
  const endingWithin = filterValue("endingWithinDays");
  const filters: ServiceAgreementFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    ...(isStatus(status) ? { status } : {}),
    ...(endingWithin !== "" && Number.isFinite(Number(endingWithin))
      ? { endingWithinDays: Number(endingWithin) }
      : {}),
    page,
  };

  const agreements = useQuery(serviceAgreementsQuery(client, filters));
  const columnCount = isSuperAdmin ? 7 : 6;
  const drawAction = canDraw ? (
    <Button onClick={() => setDrawing(true)}>{t("drawUp")}</Button>
  ) : null;

  return (
    <>
      {embedded ? (
        drawAction ? <div className="mb-4 flex justify-end">{drawAction}</div> : null
      ) : (
        <PageHeader
          title={t("listTitle")}
          description={t("listDescription")}
          action={drawAction}
        />
      )}

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchPlaceholder")}
              onChange={(value) => setFilter("search", value)}
            />
            <SelectField
              label={t("statusFilter")}
              value={status}
              onChange={(value) => setFilter("status", value)}
              data={[
                { value: "", label: t("anyStatus") },
                ...SERVICE_AGREEMENT_STATUSES.map((option) => ({
                  value: option,
                  label: SERVICE_STATUS_LABELS[option],
                })),
              ]}
            />
            <SelectField
              label={t("endingWithin")}
              value={endingWithin}
              onChange={(value) => setFilter("endingWithinDays", value)}
              data={[
                { value: "", label: t("endingAny") },
                { value: "0", label: t("endingDue") },
                { value: "30", label: t("ending30") },
                { value: "60", label: t("ending60") },
                { value: "90", label: t("ending90") },
              ]}
            />
            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {agreements.isPending ? <LoadingBlock label={t("loading")} /> : null}
      {agreements.isError ? (
        <ErrorBlock error={agreements.error} onRetry={() => void agreements.refetch()} />
      ) : null}

      {agreements.data ? (
        <Card>
          <DataTable caption={t("listTitle")}>
            <thead>
              <tr>
                {isSuperAdmin ? <Th>{t("metaOrganization")}</Th> : null}
                <Th>{t("colNumber")}</Th>
                <Th>{t("colCustomer")}</Th>
                <Th>{t("colSite")}</Th>
                <Th>{t("colAbono")}</Th>
                <Th>{t("colStatus")}</Th>
                <Th>{t("colEnds")}</Th>
              </tr>
            </thead>
            <tbody>
              {agreements.data.data.length === 0 ? (
                <EmptyRow colSpan={columnCount}>{t("emptyList")}</EmptyRow>
              ) : null}
              {agreements.data.data.map((row) => (
                <tr key={row.id}>
                  {isSuperAdmin ? <Td>{row.organizationName}</Td> : null}
                  <Td className="font-medium">
                    <Link
                      to={`/service-agreements/${row.id}`}
                      className="text-brand underline underline-offset-2"
                    >
                      {row.number}
                    </Link>
                  </Td>
                  <Td>{row.customerName}</Td>
                  <Td>{row.siteName}</Td>
                  <Td>
                    {row.currency} {row.monthlyAbonoUsd.toFixed(2)}
                  </Td>
                  <Td>
                    <Badge tone={SERVICE_STATUS_TONES[row.status]}>
                      {SERVICE_STATUS_LABELS[row.status]}
                    </Badge>
                  </Td>
                  <Td>{row.endsOn ? formatDate(row.endsOn) : EM_DASH}</Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <Td colSpan={columnCount} className="border-b-0">
                  <Pagination
                    page={agreements.data.pagination.page}
                    totalPages={agreements.data.pagination.totalPages}
                    total={agreements.data.pagination.total}
                    noun={{ one: "agreement", other: "agreements" }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <ServiceAgreementCreateDialog open={drawing} onClose={() => setDrawing(false)} />
    </>
  );
}

function isStatus(value: string): value is (typeof SERVICE_AGREEMENT_STATUSES)[number] {
  return (SERVICE_AGREEMENT_STATUSES as readonly string[]).includes(value);
}
